import React, { useState, useRef, useEffect } from "react";
import { Sheet } from "react-modal-sheet";
import { useLocation } from "react-router-dom";
import "./inputVoice.css";
import { useLanguage } from "../../context/localeContext";
import { Loader } from "rsuite";
import { useTranslation } from "react-i18next";
import { MediaRecorder as WavMediaRecorder, register } from "extendable-media-recorder";
import { connect } from "extendable-media-recorder-wav-encoder";

let wavEncoderRegistration = null;

const ensureWavEncoder = () => {
  if (!wavEncoderRegistration) {
    wavEncoderRegistration = Promise.resolve(connect()).then((port) => register(port));
  }
  return wavEncoderRegistration;
};

const InputVoice = ({ onNavigateForward, notFixed, ml_page, setJsonForm, setRefresh, loading }) => {
  const [isOpen, setOpen] = useState(false);
  const [isAnalyzing, setAnalyzing] = useState(false);
  const location = useLocation();
  const { t } = useTranslation("basic");
  
  const handleOpen = () => {
    setOpen(true);
    if (setRefresh) setRefresh(r => r + 1);
  };
  const handleClose = () => setOpen(false);

  const contentRef = useRef(null);

  const audioChunks = useRef([]);
  const [recording, setRecording] = useState(false);
  const { hasPermission, setHasPermission, mediaRecorder, setMediaRecorder, language } = useLanguage();
  const recordingRef = useRef(false);
  const isHoldingRef = useRef(false);
  const recorderRef = useRef(mediaRecorder);
  const languageRef = useRef(language);

  useEffect(() => {
    recorderRef.current = mediaRecorder;
  }, [mediaRecorder]);

  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  // Field configurations for different routes
  const getFieldConfig = () => {
    const currentPath = location.pathname;

    // Invoice creation and template creation/edit routes
    if (
      currentPath === "/invoice" ||
      currentPath === "/template" ||
      currentPath === "/template_update" ||
      currentPath.startsWith("/template_update/")
    ) {
      return [
        t("voice_from_to"),
        t("voice_place_and_goods_params"),
        t("voice_tariffs"),
        t("voice_additional_services"),
        t("voice_receiver"),
        t("voice_sender"),
      ];
    }

    // Invoice calculation route
    if (currentPath === "/invoice_calculation") {
      return [t("city_from"), t("city_to"), t("voice_cargo_weight")];
    }

    // Claim creation route
    if (currentPath === "/pretense") {
      return [
        t("voice_invoice_number"),
        t("voice_problem_description"),
        t("voice_damage_amount"),
        t("voice_contact_details"),
        t("voice_additional_info"),
      ];
    }

    // Default fields for other routes
    return [
      t("voice_from_to"),
      t("voice_place_and_goods_params"),
      t("voice_tariffs"),
      t("voice_additional_services"),
      t("voice_receiver"),
      t("voice_sender"),
    ];
  };

  const fieldTags = getFieldConfig();

  const configureRecorder = (recorder) => {
    recorder.ondataavailable = (event) => {
      audioChunks.current.push(event.data);
    };

    recorder.onstop = () => {
      const audioBlob = new Blob(audioChunks.current, { type: 'audio/wav' });
      audioChunks.current = [];
      recordingRef.current = false;
      setRecording(false);

      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.wav');
      setAnalyzing(true);

      const currentLanguage = languageRef.current;
      const lang = currentLanguage === 'kz' ? 'kz' : currentLanguage === 'en' ? 'en' : 'ru';
      const url = `${process.env.REACT_APP_API_URL}/api/ml/jsonify/${ml_page}?lang=${lang}`;

      window.fetch(url, { method: 'POST', body: formData })
        .then((response) => {
          if (!response.ok) throw new Error(`Voice request failed: ${response.status}`);
          return response.json();
        })
        .then((data) => {
          if (!data || !data.json) throw new Error('Voice response is empty');
          setJsonForm(data.json);
          setAnalyzing(false);
          handleClose();
        })
        .catch(() => {
          setAnalyzing(false);
        });
    };
  };

  const startRecording = (recorder) => {
    if (recorder && recorder.state === 'inactive' && isHoldingRef.current) {
      audioChunks.current = [];
      configureRecorder(recorder);
      recorder.start();
      recordingRef.current = true;
      setRecording(true);
    }
  };

  const requestPermission = () => {
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then((stream) => {
        ensureWavEncoder()
        .then(() => {
          const recorder = new WavMediaRecorder(stream, { mimeType: 'audio/wav' });
          configureRecorder(recorder);
          recorderRef.current = recorder;
          setMediaRecorder(recorder);
          setHasPermission(true);
          // The browser permission dialog can outlive the pointer gesture.
          // Keep the initialized recorder for the next hold, but only start
          // recording if the user is still holding the button.
          if (isHoldingRef.current) startRecording(recorder);
        })
        .catch((error) => {
          stream.getTracks().forEach((track) => track.stop());
          console.error('Unable to initialize WAV recorder:', error);
        });
      })
      .catch((error) => {
        console.error('Microphone access denied:', error);
      });
  };

  const handleInteractionStart = (event) => {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    isHoldingRef.current = true;
    const recorder = recorderRef.current;
    if (hasPermission && recorder) {
      startRecording(recorder);
    } else {
      requestPermission();
    }
  };

  const handleInteractionEnd = (event) => {
    if (!isHoldingRef.current) return;
    if (event.pointerId != null) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    isHoldingRef.current = false;
    const recorder = recorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.stop();
    }
    recordingRef.current = false;
    setRecording(false);
  };

  return (
    <>
      {/* Контейнер с кнопками */}
      <div className={`voice-navigation-container ${notFixed ? "notFixed" : ""}`} onClick={(e) => {
        console.log(e)
        e.stopPropagation();
      }}>
          <button className="voice-button" onClick={handleOpen}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.39795 11.1772C4.66378 13.0037 5.57833 14.6734 6.9743 15.8809C8.37026 17.0884 10.1543 17.753 12.0001 17.753M12.0001 17.753C13.8458 17.753 15.6298 17.0884 17.0258 15.8809C18.4218 14.6734 19.3363 13.0037 19.6022 11.1772M12.0001 17.753V21.6M12.0011 2.40002C11.1282 2.40002 10.291 2.7468 9.67376 3.36406C9.05649 3.98132 8.70972 4.81851 8.70972 5.69145V10.08C8.70972 10.953 9.05649 11.7902 9.67376 12.4074C10.291 13.0247 11.1282 13.3715 12.0011 13.3715C12.8741 13.3715 13.7113 13.0247 14.3285 12.4074C14.9458 11.7902 15.2926 10.953 15.2926 10.08V5.69145C15.2926 4.81851 14.9458 3.98132 14.3285 3.36406C13.7113 2.7468 12.8741 2.40002 12.0011 2.40002Z" stroke="#17C653" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>

        <button className="navigation-button" onClick={onNavigateForward}>
          { !loading ?
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.6126 6.00962V9.29305H2.37848C2.18971 9.29305 2.00868 9.36803 1.87521 9.50151C1.74173 9.63498 1.66675 9.81601 1.66675 10.0048C1.66675 10.1935 1.74173 10.3746 1.87521 10.508C2.00868 10.6415 2.18971 10.7165 2.37848 10.7165H12.6126V13.9904C12.6115 14.08 12.6374 14.1678 12.687 14.2424C12.7365 14.317 12.8073 14.375 12.8903 14.4087C12.9732 14.4425 13.0644 14.4506 13.152 14.4318C13.2395 14.4131 13.3194 14.3684 13.3812 14.3036L18.2002 10.3179C18.2424 10.2764 18.2759 10.2269 18.2988 10.1724C18.3216 10.1178 18.3334 10.0592 18.3334 10C18.3334 9.94086 18.3216 9.88227 18.2988 9.82769C18.2759 9.77311 18.2424 9.72363 18.2002 9.68213L13.3812 5.69646C13.3194 5.63167 13.2395 5.58699 13.152 5.56824C13.0644 5.54949 12.9732 5.55753 12.8903 5.59131C12.8073 5.6251 12.7365 5.68307 12.687 5.75768C12.6374 5.83228 12.6115 5.92007 12.6126 6.00962Z" fill="white"/>
          </svg> :
          <Loader />
          }
        </button>
      </div>

      {/* Панель для голосового ввода */}
      <Sheet isOpen={isOpen} onClose={handleClose}>
        <Sheet.Container>
          <Sheet.Content>
            <div className="input-voice-panel" ref={contentRef}>
              <h3 className="panel-title">{t("voice_panel_title")}</h3>
              <div className="tags-container">
                {fieldTags.map((tag, index) => (
                  <button key={index} className="tag">{tag}</button>
                ))}
              </div>
              <div className={`voice-record-section ${isAnalyzing ? "analyzing" : ""}`}>
                {isAnalyzing ? (
                  <div className="analyzing-container">
                    <p className="analyzing-text">{t("voice_analyzing")}</p>
                  </div>
                ) : (
                  <>
                  <button className="record-button"
                    onPointerDown={handleInteractionStart}
                    onPointerUp={handleInteractionEnd}
                    onPointerCancel={handleInteractionEnd}
                    onLostPointerCapture={handleInteractionEnd}
                    onContextMenu={(e) => e.preventDefault()}>
                    {
                      hasPermission ?
                      <>
                        <span className="record-button-icon" aria-hidden="true">
                        <svg
                          width="40"
                          height="50"
                          viewBox="0 0 40 50"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M0.994629 22.9429C1.6592 27.5091 3.94559 31.6835 7.4355 34.7022C10.9254 37.721 15.3855 39.3823 19.9999 39.3823M19.9999 39.3823C24.6142 39.3823 29.0744 37.721 32.5643 34.7022C36.0542 31.6835 38.3406 27.5091 39.0051 22.9429M19.9999 39.3823V49M20.0026 1C17.8203 1 15.7273 1.86694 14.1841 3.41009C12.641 4.95325 11.7741 7.04622 11.7741 9.22857V20.2C11.7741 22.3824 12.641 24.4753 14.1841 26.0185C15.7273 27.5616 17.8203 28.4286 20.0026 28.4286C22.185 28.4286 24.278 27.5616 25.8211 26.0185C27.3643 24.4753 28.2312 22.3824 28.2312 20.2V9.22857C28.2312 7.04622 27.3643 4.95325 25.8211 3.41009C24.278 1.86694 22.185 1 20.0026 1Z"
                            stroke="#17C653"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        </span>
                        <span className="record-button-text">
                          {recording ?
                            t("voice_record_release") :
                            t("voice_record_hold")
                          }
                        </span>
                      </> :
                      <span>{t("voice_allow_access")}</span>
                    }
                  </button>
                  </>
                )}
              </div>

             </div> 

          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop onClick={handleClose} />
      </Sheet>
    </>
  );
};

export default InputVoice;
